/**
 * Earnings Hooks
 * React Query hooks for Stripe Connect and earnings management.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as earningsService from "../services/earnings.service"

export function useConnectStatus() {
  return useQuery({
    queryKey: ["earnings", "connect-status"],
    queryFn: earningsService.getConnectStatus,
    staleTime: 30 * 1000,
  })
}

export function useEarningsSummary() {
  return useQuery({
    queryKey: ["earnings", "summary"],
    queryFn: earningsService.getEarningsSummary,
    staleTime: 60 * 1000,
  })
}

export function useTransactions(tab: string, page: number, limit = 20, search?: string) {
  return useQuery({
    queryKey: ["earnings", "transactions", tab, page, limit, search],
    queryFn: () => earningsService.getTransactions(tab, page, limit, search),
    staleTime: 30 * 1000,
  })
}

export function useCreateConnectAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ returnUrl, refreshUrl }: { returnUrl: string; refreshUrl: string }) =>
      earningsService.createConnectAccount(returnUrl, refreshUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["earnings", "connect-status"] })
    },
  })
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: earningsService.disconnectAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["earnings"] })
    },
  })
}

export function useRequestPayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: earningsService.requestPayout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["earnings"] })
    },
  })
}

export function useDashboardLink() {
  return useMutation({
    mutationFn: earningsService.getDashboardLink,
  })
}

export function useTransactionDetail(transactionId: string) {
  return useQuery({
    queryKey: ["earnings", "transaction", transactionId],
    queryFn: () => earningsService.getTransactionById(transactionId),
    enabled: !!transactionId,
    staleTime: 30 * 1000,
  })
}

export function useRelatedTransactions(transactionId: string) {
  return useQuery({
    queryKey: ["earnings", "transaction", transactionId, "related"],
    queryFn: () => earningsService.getRelatedTransactions(transactionId),
    enabled: !!transactionId,
    staleTime: 30 * 1000,
  })
}

export function useRefundTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: earningsService.refundTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["earnings", "transaction"] })
    },
  })
}
